// src/pages/tickets/AdminTicketDetailPage.tsx

import {
  AlertCircle,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  RefreshCw,
  Router,
  Send,
  ShieldCheck,
  Smartphone,
  Ticket,
  User,
  UserCheck,
  Wallet,
  Wifi,
  X,
  XCircle,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../components/ui/Toast";
import { fetchApp } from "../../services/apiService";

interface Technician {
  id: string;
  username: string;
  email?: string | null;
}

interface TicketUpdate {
  id: string;
  ticket_id: string;
  portal_account_id?: string | null;
  user_id?: string | null;
  update_type: string;
  message: string;
  created_at?: string | null;
}

interface TicketAssignment {
  id: string;
  ticket_id: string;
  assigned_by: string;
  technician_id: string;
  assignment_type: string;
  status: string;
  assignment_note?: string | null;
  assigned_at?: string | null;
  accepted_at?: string | null;
  rejected_at?: string | null;
  rejection_reason?: string | null;
  completed_at?: string | null;
  is_current: boolean;
}

interface TicketCustomer {
  id: string;
  client_id: string;
  customer_id: string;
  secret_id: string;
  full_name: string;
  phone_number: string;
  email?: string | null;
  is_active: boolean;
  last_login_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface TicketSecret {
  id: string;
  router_id: string;
  router_name?: string | null;
  username: string;
  plan_id?: string | null;
  plan_name?: string | null;
  plan_price?: number | null;
  plan_speed?: string | null;
  amount_payable: number;
  status: boolean;
  account_number: string;
  phone_number: string;
  email: string;
  customer_id: string;
  service_expires_at?: string | null;
  access_expires_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface AdminTicketDetail {
  id: string;
  client_id: string;
  portal_account_id: string;
  ticket_number: string;
  subject: string;
  category?: string | null;
  priority: string;
  message: string;
  status: string;
  is_outsourced: boolean;
  resolved_at?: string | null;
  closed_at?: string | null;
  last_updated_at?: string | null;
  latitude?: string | number | null;
  longitude?: string | number | null;
  location_name?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  assignments: TicketAssignment[];
  updates: TicketUpdate[];
  customer?: TicketCustomer | null;
  secret?: TicketSecret | null;
}

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const textareaCls =
  "w-full resize-none rounded-2xl border border-slate-300 bg-slate-50 px-3.5 py-3 text-[13px] font-medium leading-relaxed text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatMoney = (value?: number | null) => {
  if (value === null || value === undefined) return "—";
  return `KES ${Number(value).toLocaleString("en-KE")}`;
};

const formatText = (value?: string | null) =>
  String(value || "—")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getStatusTone = (status?: string | null) => {
  const value = String(status || "").toLowerCase();

  if (value === "closed") return "border-slate-300 bg-slate-200 text-slate-700";
  if (value === "resolved" || value === "customer_confirmed")
    return "border-emerald-200 bg-emerald-100 text-emerald-800";
  if (
    value === "assigned" ||
    value === "accepted" ||
    value === "in_progress" ||
    value === "technician_completed"
  )
    return "border-sky-200 bg-sky-100 text-sky-800";
  if (value === "cancelled" || value === "rejected")
    return "border-rose-200 bg-rose-100 text-rose-700";

  return "border-amber-200 bg-amber-100 text-amber-800";
};

const getPriorityTone = (priority?: string | null) => {
  const value = String(priority || "").toLowerCase();

  if (value === "urgent" || value === "high")
    return "border-rose-200 bg-rose-100 text-rose-700";
  if (value === "medium") return "border-amber-200 bg-amber-100 text-amber-800";

  return "border-emerald-200 bg-emerald-100 text-emerald-800";
};

const StatusBadge: React.FC<{ status?: string | null }> = ({ status }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-black ${getStatusTone(
      status,
    )}`}
  >
    <span className="h-1.5 w-1.5 rounded-full bg-current" />
    {formatText(status)}
  </span>
);

const PriorityBadge: React.FC<{ priority?: string | null }> = ({
  priority,
}) => (
  <span
    className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-black ${getPriorityTone(
      priority,
    )}`}
  >
    {formatText(priority)}
  </span>
);

const ActiveBadge: React.FC<{
  active?: boolean;
  activeText: string;
  inactiveText: string;
}> = ({ active, activeText, inactiveText }) => {
  if (active) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-[11px] font-black text-emerald-800">
        <CheckCircle2 className="h-3 w-3" />
        {activeText}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-100 px-2.5 py-1 text-[11px] font-black text-rose-700">
      <XCircle className="h-3 w-3" />
      {inactiveText}
    </span>
  );
};

const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
    <div className="mx-auto flex min-h-[60vh] max-w-[1600px] items-center justify-center">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />
        <div className="relative overflow-hidden px-6 py-10 text-center">
          <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-emerald-200/35 blur-2xl" />
          <div className="absolute -bottom-20 left-4 h-52 w-52 rounded-full bg-cyan-200/35 blur-2xl" />

          <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/50">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>

          <p className="relative mt-4 text-[15px] font-black text-slate-900">
            Loading ticket details…
          </p>
          <p className="relative mt-1 text-[12.5px] font-medium text-slate-500">
            Please wait while ticket information is being prepared.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const InfoCard: React.FC<{
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, icon, children }) => (
  <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
    <div className="border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
          {icon}
        </div>

        <div>
          <p className="text-[14px] font-black text-slate-900">{title}</p>
          <p className="text-[11.5px] font-medium text-slate-500">{subtitle}</p>
        </div>
      </div>
    </div>

    <div className="p-4 sm:p-5">{children}</div>
  </div>
);

const DetailItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  tone?: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ icon, label, value, tone = "slate" }) => {
  const tones = {
    slate: "border-slate-200 bg-slate-100",
    emerald: "border-emerald-200 bg-emerald-50",
    sky: "border-sky-200 bg-sky-50",
    amber: "border-amber-200 bg-amber-50",
    rose: "border-rose-200 bg-rose-50",
  };

  return (
    <div className={`rounded-2xl border px-3 py-3 ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
        <span className="text-slate-400">{icon}</span>
        {label}
      </div>

      <div className="mt-1 break-words text-[13px] font-black text-slate-900">
        {value || "—"}
      </div>
    </div>
  );
};

const SupportTicketDetailPage: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [ticket, setTicket] = useState<AdminTicketDetail | null>(null);
  const [technicians, setTechnicians] = useState<Technician[]>([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [technicianId, setTechnicianId] = useState("");
  const [assignmentNote, setAssignmentNote] = useState("");
  const [comment, setComment] = useState("");

  const [assigning, setAssigning] = useState(false);
  const [commenting, setCommenting] = useState(false);
  const [closing, setClosing] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const customer = ticket?.customer || null;
  const secret = ticket?.secret || null;

  const loadTechnicians = async () => {
    try {
      const res = await fetchApp<Technician[]>("/admin/technicians/");
      setTechnicians(Array.isArray(res) ? res : []);
    } catch (err: any) {
      showToast(err?.message || "Failed to load technicians.", "error");
    }
  };

  const loadTicket = async (manual = false) => {
    if (!ticketId) {
      setError("Missing ticket ID.");
      setLoading(false);
      return;
    }

    try {
      if (manual) setRefreshing(true);
      else setLoading(true);

      setError(null);

      const res = await fetchApp<AdminTicketDetail>(
        `/admin/tickets/${ticketId}`,
      );

      setTicket(res);
    } catch (err: any) {
      const msg = err?.message || "Failed to load ticket details.";
      setError(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadTicket(false);
    loadTechnicians();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  const getTechnicianName = (id?: string | null) => {
    if (!id) return "—";
    const tech = technicians.find((item) => item.id === id);
    if (!tech) return id;
    return `${tech.username}${tech.email ? ` (${tech.email})` : ""}`;
  };

  const currentAssignment = useMemo(() => {
    return ticket?.assignments?.find((item) => item.is_current) || null;
  }, [ticket]);

  const timeline = useMemo(() => {
    const assignmentItems =
      ticket?.assignments?.map((item) => ({
        id: item.id,
        type: "assignment",
        title: `Assignment ${formatText(item.status)}`,
        message:
          item.assignment_note ||
          `Assigned to ${getTechnicianName(item.technician_id)}`,
        date: item.assigned_at,
        tone: "sky" as const,
      })) || [];

    const updateItems =
      ticket?.updates?.map((item) => ({
        id: item.id,
        type: item.update_type,
        title: formatText(item.update_type),
        message: item.message,
        date: item.created_at,
        tone:
          item.update_type === "staff_comment"
            ? ("emerald" as const)
            : ("amber" as const),
      })) || [];

    return [...assignmentItems, ...updateItems].sort((a, b) => {
      const dateA = new Date(a.date || "").getTime();
      const dateB = new Date(b.date || "").getTime();
      return dateB - dateA;
    });
  }, [ticket, technicians]);

  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!ticket) return;

    if (!technicianId.trim()) {
      showToast("Please select a technician.", "error");
      return;
    }

    try {
      setAssigning(true);

      await fetchApp(`/admin/tickets/${ticket.id}/assign`, {
        method: "POST",
        body: JSON.stringify({
          technician_id: technicianId,
          assignment_note: assignmentNote.trim() || null,
        }),
      });

      showToast("Ticket assigned successfully.", "success");

      setTechnicianId("");
      setAssignmentNote("");

      await loadTicket(true);
    } catch (err: any) {
      showToast(err?.message || "Failed to assign ticket.", "error");
    } finally {
      setAssigning(false);
    }
  };

  const handleComment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!ticket) return;

    if (!comment.trim()) {
      showToast("Comment message is required.", "error");
      return;
    }

    try {
      setCommenting(true);

      await fetchApp(`/admin/tickets/${ticket.id}/comment`, {
        method: "POST",
        body: JSON.stringify({
          message: comment.trim(),
        }),
      });

      showToast("Comment added successfully.", "success");

      setComment("");

      await loadTicket(true);
    } catch (err: any) {
      showToast(err?.message || "Failed to add comment.", "error");
    } finally {
      setCommenting(false);
    }
  };

  const handleClose = async () => {
    if (!ticket) return;

    const ok = window.confirm(
      `Are you sure you want to close ticket ${ticket.ticket_number}?`,
    );

    if (!ok) return;

    try {
      setClosing(true);

      await fetchApp(`/admin/tickets/${ticket.id}/close`, {
        method: "POST",
      });

      showToast("Ticket closed successfully.", "success");

      await loadTicket(true);
    } catch (err: any) {
      showToast(err?.message || "Failed to close ticket.", "error");
    } finally {
      setClosing(false);
    }
  };

  if (loading && !ticket) return <LoadingState />;

  if (!ticket) {
    return (
      <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
        <div className="mx-auto max-w-[900px] rounded-3xl border border-rose-200 bg-rose-50 p-6 text-rose-700 shadow-sm">
          <p className="font-black">Ticket not found</p>

          <p className="mt-1 text-sm">
            {error || "The selected ticket could not be loaded."}
          </p>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className={`${btnBase} mt-4 h-10 border border-rose-200 bg-white px-4 text-rose-700 hover:bg-rose-100`}
          >
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const hasCoordinates = ticket.latitude != null && ticket.longitude != null;

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="mb-3 inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-[12px] font-black text-slate-600 shadow-sm transition hover:bg-slate-100 hover:text-slate-900"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Tickets
                </button>

                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Support Ticket Detail
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <h1 className="truncate text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                    {ticket.ticket_number}
                  </h1>

                  <StatusBadge status={ticket.status} />
                  <PriorityBadge priority={ticket.priority} />
                </div>

                <p className="mt-1 max-w-3xl text-[14px] font-black text-slate-800">
                  {ticket.subject}
                </p>

                <p className="mt-1 max-w-3xl text-[13px] font-medium text-slate-500">
                  {ticket.message}
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={() => loadTicket(true)}
                  disabled={refreshing}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  {refreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Refresh
                </button>

                <button
                  type="button"
                  onClick={handleClose}
                  disabled={closing || ticket.status === "closed"}
                  className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600 disabled:bg-slate-300 disabled:shadow-none`}
                >
                  {closing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="h-4 w-4" />
                  )}
                  {ticket.status === "closed" ? "Closed" : "Close Ticket"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {refreshing && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing ticket details…
          </div>
        )}

        {error && (
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={() => setError(null)}
              className="rounded-xl p-1.5 text-rose-600 transition-colors hover:bg-rose-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Summary */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:gap-4">
          <StatCard
            label="Ticket Status"
            value={formatText(ticket.status)}
            sub="Current ticket stage"
            icon={<Ticket className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="Requester"
            value={customer?.full_name || "—"}
            sub={customer?.phone_number || "No phone"}
            icon={<User className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Internet Account"
            value={secret?.account_number || "—"}
            sub={secret?.username || "No username"}
            icon={<Wifi className="h-5 w-5" />}
            tone={secret?.status ? "emerald" : "rose"}
          />

          <StatCard
            label="Technician"
            value={currentAssignment ? "Assigned" : "Unassigned"}
            sub={
              currentAssignment
                ? getTechnicianName(currentAssignment.technician_id)
                : "No technician assigned"
            }
            icon={<UserCheck className="h-5 w-5" />}
            tone={currentAssignment ? "sky" : "amber"}
          />
        </div>

        {/* Proper categorized layout */}
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <InfoCard
            title="Requester Details"
            subtitle="Customer portal account that opened this ticket."
            icon={<User className="h-5 w-5" />}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <DetailItem
                icon={<User className="h-3.5 w-3.5" />}
                label="Full Name"
                value={customer?.full_name}
                tone="emerald"
              />

              <DetailItem
                icon={<Phone className="h-3.5 w-3.5" />}
                label="Portal Phone"
                value={customer?.phone_number}
                tone="sky"
              />

              <DetailItem
                icon={<Mail className="h-3.5 w-3.5" />}
                label="Portal Email"
                value={customer?.email}
              />

              <DetailItem
                icon={<ShieldCheck className="h-3.5 w-3.5" />}
                label="Portal Status"
                value={
                  <ActiveBadge
                    active={customer?.is_active}
                    activeText="Active"
                    inactiveText="Inactive"
                  />
                }
                tone={customer?.is_active ? "emerald" : "rose"}
              />

              <DetailItem
                icon={<Clock className="h-3.5 w-3.5" />}
                label="Last Login"
                value={formatDateTime(customer?.last_login_at)}
              />

              <DetailItem
                icon={<CalendarDays className="h-3.5 w-3.5" />}
                label="Joined Portal"
                value={formatDateTime(customer?.created_at)}
              />
            </div>
          </InfoCard>

          <InfoCard
            title="PPPoE Service Details"
            subtitle="Internet account linked to the requester."
            icon={<Wifi className="h-5 w-5" />}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <DetailItem
                icon={<Wifi className="h-3.5 w-3.5" />}
                label="Username"
                value={secret?.username}
                tone="emerald"
              />

              <DetailItem
                icon={<Wallet className="h-3.5 w-3.5" />}
                label="Account No."
                value={secret?.account_number}
                tone="sky"
              />

              <DetailItem
                icon={<Router className="h-3.5 w-3.5" />}
                label="Router"
                value={secret?.router_name}
              />

              <DetailItem
                icon={<Smartphone className="h-3.5 w-3.5" />}
                label="Secret Phone"
                value={secret?.phone_number}
              />

              <DetailItem
                icon={<Mail className="h-3.5 w-3.5" />}
                label="Secret Email"
                value={secret?.email}
              />

              <DetailItem
                icon={<ShieldCheck className="h-3.5 w-3.5" />}
                label="Secret Status"
                value={
                  <ActiveBadge
                    active={secret?.status}
                    activeText="Active"
                    inactiveText="Inactive"
                  />
                }
                tone={secret?.status ? "emerald" : "rose"}
              />
            </div>
          </InfoCard>

          <InfoCard
            title="Plan & Billing"
            subtitle="Plan, payable amount and expiry information."
            icon={<Wallet className="h-5 w-5" />}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <DetailItem
                icon={<Wifi className="h-3.5 w-3.5" />}
                label="Plan"
                value={secret?.plan_name}
                tone="amber"
              />

              <DetailItem
                icon={<Wifi className="h-3.5 w-3.5" />}
                label="Speed"
                value={secret?.plan_speed}
                tone="sky"
              />

              <DetailItem
                icon={<Wallet className="h-3.5 w-3.5" />}
                label="Plan Price"
                value={formatMoney(secret?.plan_price)}
                tone="emerald"
              />

              <DetailItem
                icon={<Wallet className="h-3.5 w-3.5" />}
                label="Amount Payable"
                value={formatMoney(secret?.amount_payable)}
                tone="amber"
              />

              <DetailItem
                icon={<Clock className="h-3.5 w-3.5" />}
                label="Service Expiry"
                value={formatDateTime(secret?.service_expires_at)}
              />

              <DetailItem
                icon={<Clock className="h-3.5 w-3.5" />}
                label="Access Expiry"
                value={formatDateTime(secret?.access_expires_at)}
              />
            </div>
          </InfoCard>

          <InfoCard
            title="Ticket Information"
            subtitle="Issue category, date, location and map details."
            icon={<Ticket className="h-5 w-5" />}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <DetailItem
                icon={<Ticket className="h-3.5 w-3.5" />}
                label="Ticket Number"
                value={ticket.ticket_number}
                tone="sky"
              />

              <DetailItem
                icon={<MessageSquare className="h-3.5 w-3.5" />}
                label="Category"
                value={ticket.category}
              />

              <DetailItem
                icon={<CalendarDays className="h-3.5 w-3.5" />}
                label="Created"
                value={formatDateTime(ticket.created_at)}
              />

              <DetailItem
                icon={<CalendarDays className="h-3.5 w-3.5" />}
                label="Last Updated"
                value={formatDateTime(
                  ticket.last_updated_at || ticket.updated_at,
                )}
              />

              <DetailItem
                icon={<MapPin className="h-3.5 w-3.5" />}
                label="Location"
                value={ticket.location_name || "No location name"}
                tone="amber"
              />

              {hasCoordinates && (
                <DetailItem
                  icon={<MapPin className="h-3.5 w-3.5" />}
                  label="Map"
                  value={
                    <a
                      href={`https://www.google.com/maps?q=${ticket.latitude},${ticket.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-emerald-700 hover:text-emerald-800"
                    >
                      Open in Google Maps
                    </a>
                  }
                  tone="emerald"
                />
              )}
            </div>
          </InfoCard>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <InfoCard
            title="Assign Technician"
            subtitle="Select a technician and add an optional assignment note."
            icon={<UserCheck className="h-5 w-5" />}
          >
            <form onSubmit={handleAssign} className="space-y-4">
              {currentAssignment && (
                <div className="rounded-3xl border border-sky-200 bg-sky-50 px-4 py-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-sky-700">
                    Current Technician
                  </p>

                  <p className="mt-1 text-[13px] font-black text-slate-900">
                    {getTechnicianName(currentAssignment.technician_id)}
                  </p>

                  <p className="mt-1 text-[12px] font-medium text-slate-500">
                    Assigned: {formatDateTime(currentAssignment.assigned_at)}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Technician
                  </label>

                  <select
                    value={technicianId}
                    onChange={(e) => setTechnicianId(e.target.value)}
                    className={`${inputCls} cursor-pointer`}
                  >
                    <option value="">Select technician...</option>

                    {technicians.map((tech) => (
                      <option key={tech.id} value={tech.id}>
                        {tech.username}
                        {tech.email ? ` (${tech.email})` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Quick Status
                  </label>

                  <div className="flex h-10 items-center rounded-2xl border border-slate-300 bg-slate-100 px-3 text-[13px] font-black text-slate-700">
                    {currentAssignment ? "Assigned" : "Not assigned"}
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                  Assignment Note
                </label>

                <textarea
                  value={assignmentNote}
                  onChange={(e) => setAssignmentNote(e.target.value)}
                  placeholder="Optional assignment note..."
                  rows={4}
                  className={textareaCls}
                />
              </div>

              <button
                type="submit"
                disabled={assigning}
                className={`${btnBase} h-11 w-full bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
              >
                {assigning ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <UserCheck className="h-4 w-4" />
                )}
                {assigning ? "Assigning…" : "Assign Technician"}
              </button>
            </form>
          </InfoCard>

          <InfoCard
            title="Staff Comment"
            subtitle="Add an internal admin update to this ticket."
            icon={<MessageSquare className="h-5 w-5" />}
          >
            <form onSubmit={handleComment} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                  Comment
                </label>

                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Write staff comment..."
                  rows={7}
                  className={textareaCls}
                />
              </div>

              <button
                type="submit"
                disabled={commenting}
                className={`${btnBase} h-11 w-full border border-sky-200 bg-sky-100 px-5 text-sky-800 hover:bg-sky-200`}
              >
                {commenting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {commenting ? "Adding…" : "Add Comment"}
              </button>
            </form>
          </InfoCard>
        </div>

        {/* Timeline */}
        <InfoCard
          title="Ticket Timeline"
          subtitle="Assignments, customer updates and staff comments."
          icon={<Clock className="h-5 w-5" />}
        >
          {timeline.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {timeline.map((item) => (
                <div
                  key={`${item.type}-${item.id}`}
                  className="rounded-3xl border border-slate-200 bg-slate-100 px-4 py-4"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${
                        item.tone === "sky"
                          ? "bg-sky-100 text-sky-700"
                          : item.tone === "emerald"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {item.type === "assignment" ? (
                        <UserCheck className="h-4 w-4" />
                      ) : item.type === "staff_comment" ? (
                        <MessageSquare className="h-4 w-4" />
                      ) : (
                        <Ticket className="h-4 w-4" />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-[13px] font-black text-slate-900">
                          {item.title}
                        </p>

                        <p className="text-[11.5px] font-medium text-slate-500">
                          {formatDateTime(item.date)}
                        </p>
                      </div>

                      <p className="mt-2 whitespace-pre-wrap text-[13px] font-medium leading-relaxed text-slate-700">
                        {item.message || "—"}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 py-12 text-center">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                <MessageSquare className="h-7 w-7 text-slate-500" />
              </div>

              <p className="text-[14px] font-black text-slate-800">
                No timeline updates
              </p>

              <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                Updates and assignment history will appear here.
              </p>
            </div>
          )}
        </InfoCard>
      </div>
    </div>
  );
};

export default SupportTicketDetailPage;
